import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import HighchartsReact from "highcharts-react-official";
import { LuDownload, LuMaximize2, LuTable, LuX } from "react-icons/lu";
import Highcharts from "@/utils/highcharts";
import "./ChartBlock.scss";

const TOOLTIP_GAP = 10;

/**
 * A dark tooltip with an arrow, centred above the hovered bars.
 *
 * Highcharts draws its pointer only for a single-point tooltip; a shared one
 * (all brands for a category, which the agent asks for) gets no anchor, so no
 * arrow, wherever it sits. So the box is HTML — `.lou-tip` in ChartBlock.scss,
 * arrow included — wrapped around Highcharts' own formatting, which keeps the
 * agent's `valueSuffix` and formats working. It renders outside the chart so
 * it can rise above tall bars instead of covering them, and flips below only
 * when it would leave the top of the screen.
 */
const TOOLTIP = {
  shared: true,
  useHTML: true,
  outside: true,
  backgroundColor: "transparent",
  borderWidth: 0,
  shadow: false,
  padding: 0,
  // Above the Lou panel, whose layers reach 9999.
  style: { zIndex: 10000 },
  formatter(tooltip) {
    const parts = tooltip.defaultFormatter.call(this, tooltip);
    return `<div class="lou-tip">${Array.isArray(parts) ? parts.join("") : parts}</div>`;
  },
  positioner(width, height, point) {
    const chart = this.chart;
    const hovered = chart.hoverPoints || [];
    const anchorX = chart.plotLeft + point.plotX;
    // The top of the tallest hovered bar; an inverted chart's points are
    // measured the other way round, so it keeps the point Highcharts gave.
    const top =
      chart.plotTop +
      (hovered.length && !chart.inverted ? Math.min(...hovered.map((p) => p.plotY)) : point.plotY);
    const below = chart.container.getBoundingClientRect().top + top - height - TOOLTIP_GAP < 0;
    const x = Math.min(Math.max(anchorX - width / 2, 0), chart.chartWidth - width);

    const box = this.label?.text?.element?.querySelector?.(".lou-tip");
    if (box) {
      box.classList.toggle("is-below", below);
      box.style.setProperty("--lou-tip-arrow", `${anchorX - x}px`);
    }
    return { x, y: below ? top + TOOLTIP_GAP : top - height - TOOLTIP_GAP };
  },
};

/**
 * How a chart looks inside a Lou reply, beneath whatever the agent sends.
 *
 * The first colour is the brand the question is about; the rest step down in
 * lightness so the focus brand reads first even in greyscale. The agent's own
 * `colors` or a series `color` still win — this only fills what it left out.
 */
const LOU_CHART_THEME = {
  colors: ["#0A8F76", "#7A9BCB", "#C9CCD4", "#2F4A73", "#E8A33D", "#9B7BC8"],
  chart: {
    backgroundColor: "transparent",
    spacing: [12, 8, 8, 4],
    style: { fontFamily: '"Bricolage Grotesque", -apple-system, "Segoe UI", sans-serif' },
  },
  // Above the plot, where it is read before the bars.
  legend: {
    align: "left",
    verticalAlign: "top",
    symbolRadius: 3,
    itemStyle: { fontSize: "12px", fontWeight: "500", color: "#2A2D35" },
  },
  tooltip: TOOLTIP,
  plotOptions: {
    series: { borderRadius: 3 },
  },
};

// No ellipsis: "Advocacy (Recomme…" hid the half of the label that said what
// it was. Long category names wrap instead.
const X_AXIS = {
  lineColor: "#E2E4EA",
  tickLength: 0,
  labels: { style: { fontSize: "12px", color: "#2A2D35", textOverflow: "none" } },
  title: { style: { fontSize: "12px", color: "#5F6370" } },
};

const Y_AXIS = {
  gridLineColor: "#EDEEF2",
  labels: { style: { fontSize: "11px", color: "#5F6370" } },
  title: { style: { fontSize: "12px", color: "#5F6370" } },
};

/** An axis may be one object or several; each gets the theme beneath it. */
const themeAxis = (axis, base) =>
  Array.isArray(axis)
    ? axis.map((a) => Highcharts.merge(base, a))
    : Highcharts.merge(base, axis || {});

const withTheme = (config) => ({
  ...Highcharts.merge(LOU_CHART_THEME, config),
  xAxis: themeAxis(config.xAxis, X_AXIS),
  yAxis: themeAxis(config.yAxis, Y_AXIS),
  // Title and subtitle move out of the plot into the header above it, beside
  // the chart's own buttons.
  title: { text: undefined },
  subtitle: { text: undefined },
  // Export stays on, so the Download button can use it; its ☰ menu does not,
  // the header's buttons replace it.
  exporting: { enabled: true, buttons: { contextButton: { enabled: false } } },
});

const pointValue = (point) =>
  Array.isArray(point) ? point[1] : point && typeof point === "object" ? point.y : point;

const pointName = (point) =>
  Array.isArray(point) ? point[0] : point && typeof point === "object" ? point.name : undefined;

/**
 * The chart's numbers as rows and columns: one row per category, one column
 * per series. A chart without categories (a pie) names its rows by point.
 */
export const tableOf = (config = {}) => {
  const series = (config.series || []).filter((s) => Array.isArray(s.data));
  const axis = Array.isArray(config.xAxis) ? config.xAxis[0] : config.xAxis;
  const categories = axis?.categories;
  const length = Math.max(0, ...series.map((s) => s.data.length));
  const rows = Array.from({ length }, (_, i) => ({
    label: categories?.[i] ?? pointName(series[0]?.data[i]) ?? String(i + 1),
    values: series.map((s) => pointValue(s.data[i])),
  }));
  return { columns: series.map((s, i) => s.name || `Series ${i + 1}`), rows };
};

const formatValue = (v, suffix) =>
  typeof v === "number" ? `${Number.isInteger(v) ? v : v.toFixed(1)}${suffix}` : "—";

const DataTable = ({ config }) => {
  const { columns, rows } = tableOf(config);
  const suffix = config.tooltip?.valueSuffix ?? "";
  return (
    <div className="chart-block__tableWrap">
      <table className="chart-block__table">
        <thead>
          <tr>
            <th scope="col" />
            {columns.map((c) => (
              <th scope="col" key={c}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {row.values.map((v, i) => (
                <td key={columns[i]}>{formatValue(v, suffix)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Hides the chart's tooltip as soon as anything scrolls.
 *
 * The tooltip is drawn outside the chart, on the page, so it does not travel
 * with the conversation when it scrolls. Highcharts only hides it once the
 * pointer moves off the chart, and then after a delay, so it hung in mid-air
 * for seconds. Scroll events do not bubble; listening on the capture phase
 * catches every scrolling container.
 */
const useHideTooltipOnScroll = (chartRef) => {
  useEffect(() => {
    const hide = () => {
      const chart = chartRef.current?.chart;
      if (!chart || chart.tooltip?.isHidden !== false) return;
      chart.pointer?.reset?.(false, 0);
    };
    document.addEventListener("scroll", hide, true);
    return () => document.removeEventListener("scroll", hide, true);
  }, [chartRef]);
};

/**
 * The buttons above a chart: table view, download, and either expand (in the
 * reply) or close (expanded). `getChart` is read on click, so it always finds
 * the chart that is on screen.
 */
const ChartTools = ({ view, setView, getChart, fileName, onExpand, expandRef, onClose, closeRef }) => {
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // The download menu closes on a click anywhere else, or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : !menuRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  const download = (type) => {
    setMenuOpen(false);
    if (type === "csv") getChart()?.downloadCSV?.();
    else getChart()?.exportChart?.({ type: "image/png", filename: fileName });
  };

  return (
    <div className="chart-block__tools">
      <button
        type="button"
        className={`chart-block__tool${view === "table" ? " is-active" : ""}`}
        aria-label={view === "table" ? "Show as chart" : "Show as table"}
        aria-pressed={view === "table"}
        title={view === "table" ? "Show as chart" : "Show as table"}
        onClick={() => setView(view === "table" ? "chart" : "table")}
      >
        <LuTable size={16} aria-hidden="true" />
      </button>

      <div className="chart-block__menuWrap" ref={menuRef}>
        <button
          type="button"
          className={`chart-block__tool${menuOpen ? " is-active" : ""}`}
          aria-label="Download"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title="Download"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <LuDownload size={16} aria-hidden="true" />
        </button>
        {menuOpen ? (
          <div className="chart-block__menu" role="menu">
            <button type="button" role="menuitem" onClick={() => download("png")}>
              Image (PNG)
            </button>
            <button type="button" role="menuitem" onClick={() => download("csv")}>
              Data (CSV)
            </button>
          </div>
        ) : null}
      </div>

      {onExpand ? (
        <button
          type="button"
          className="chart-block__tool"
          aria-label="Expand chart"
          title="Expand"
          onClick={onExpand}
          ref={expandRef}
        >
          <LuMaximize2 size={16} aria-hidden="true" />
        </button>
      ) : null}
      {onClose ? (
        <button
          type="button"
          className="chart-block__tool chart-block__tool--close"
          aria-label="Close"
          title="Close"
          onClick={onClose}
          ref={closeRef}
        >
          <LuX size={18} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

/**
 * The chart opened over the app, at the width of the window.
 *
 * Not the browser's full-screen mode: that took over the whole display, which
 * read as the screen expanding rather than the chart. This is a window over
 * the page, closed with its button, Escape or a click outside it.
 */
const ExpandedChart = ({ options, config, title, subtitle, fileName, onClose }) => {
  const chartRef = useRef(null);
  const closeRef = useRef(null);
  const [view, setView] = useState("chart");
  // Read through a ref so a new handler each render does not re-run the
  // effect below, which would pull focus back to the close button.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Sized by its window rather than by the agent, and its tooltip above the
  // window rather than beneath it.
  const expandedOptions = useMemo(
    () => Highcharts.merge(options, { chart: { height: null }, tooltip: { style: { zIndex: 10060 } } }),
    [options],
  );

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => {
      // An open download menu takes Escape for itself.
      if (e.key !== "Escape" || document.querySelector(".lou-chart-modal .chart-block__menu")) return;
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (view === "chart") chartRef.current?.chart?.reflow?.();
  }, [view]);

  useHideTooltipOnScroll(chartRef);

  return createPortal(
    <div
      className="lou-chart-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <figure
        className="chart-block chart-block--themed lou-chart-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title || "Chart"}
      >
        <header className="chart-block__head">
          <figcaption className="chart-block__caption">
            {title ? <span className="chart-block__title">{title}</span> : null}
            {subtitle ? <span className="chart-block__subtitle">{subtitle}</span> : null}
          </figcaption>
          <ChartTools
            view={view}
            setView={setView}
            getChart={() => chartRef.current?.chart}
            fileName={fileName}
            onClose={onClose}
            closeRef={closeRef}
          />
        </header>
        <div
          className={`chart-block__card lou-chart-modal__card${view === "table" ? " chart-block__card--table" : ""}`}
        >
          {/* The height is inline on purpose: Highcharts only trusts a 100%
              container when its parent states a height of its own, and
              otherwise draws at its 400px default - half the window. */}
          <div
            className="chart-block__plot lou-chart-modal__plot"
            hidden={view === "table"}
            style={{ height: "100%" }}
          >
            <HighchartsReact
              highcharts={Highcharts}
              options={expandedOptions}
              ref={chartRef}
              containerProps={{ style: { height: "100%" } }}
            />
          </div>
          {view === "table" ? <DataTable config={config} /> : null}
        </div>
      </figure>
    </div>,
    document.body,
  );
};

const ChartBlock = ({ highchartsConfig, block_id, section_id, report_id, themed = false }) => {
  const config = highchartsConfig || {};
  const chartRef = useRef(null);
  const expandRef = useRef(null);
  const [view, setView] = useState("chart");
  const [expanded, setExpanded] = useState(false);

  // Keyed by content, not identity. A reply's blocks are parsed afresh on every
  // render, so the same chart arrives as a new object each time; keyed by
  // identity, Highcharts was told to update - and re-animated - on each one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const configKey = useMemo(() => JSON.stringify(config), [highchartsConfig]);
  const options = useMemo(
    () => (themed ? withTheme(config) : { ...config, exporting: { enabled: true } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configKey, themed],
  );

  useHideTooltipOnScroll(chartRef);

  // Hidden while the table showed, the chart missed any resize; it measures
  // again when it comes back.
  useEffect(() => {
    if (view === "chart") chartRef.current?.chart?.reflow?.();
  }, [view]);
  const title = config.title?.text;
  const subtitle = config.subtitle?.text;
  const fileName = (title || "lou-chart").replace(/[^\w-]+/g, "-").toLowerCase();

  const collapse = () => {
    setExpanded(false);
    expandRef.current?.focus();
  };

  const plot = <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />;

  if (!themed) {
    return (
      <div
        className="chart-block"
        id={block_id || undefined}
        data-block-id={block_id || undefined}
        data-section-id={section_id || undefined}
        data-report-id={report_id || undefined}
      >
        {plot}
      </div>
    );
  }

  return (
    <figure
      className="chart-block chart-block--themed"
      id={block_id || undefined}
      data-block-id={block_id || undefined}
      data-section-id={section_id || undefined}
      data-report-id={report_id || undefined}
    >
      <header className="chart-block__head">
        <figcaption className="chart-block__caption">
          {title ? <span className="chart-block__title">{title}</span> : null}
          {subtitle ? <span className="chart-block__subtitle">{subtitle}</span> : null}
        </figcaption>

        <ChartTools
          view={view}
          setView={setView}
          getChart={() => chartRef.current?.chart}
          fileName={fileName}
          onExpand={() => setExpanded(true)}
          expandRef={expandRef}
        />
      </header>

      {/* The chart stays mounted under the table, so download still has a
          chart to work with. */}
      <div className={`chart-block__card${view === "table" ? " chart-block__card--table" : ""}`}>
        <div className="chart-block__plot" hidden={view === "table"}>
          {plot}
        </div>
        {view === "table" ? <DataTable config={config} /> : null}
      </div>

      {expanded ? (
        <ExpandedChart
          options={options}
          config={config}
          title={title}
          subtitle={subtitle}
          fileName={fileName}
          onClose={collapse}
        />
      ) : null}
    </figure>
  );
};

export default ChartBlock;
